import { useState, useEffect, useCallback } from 'react'
import { lobbyService, LobbyStats } from '../services/lobbyService'
import { useCompanyDisabledState } from './useCompanyDisabledState'

interface UseLobbyStatsReturn {
    lobbyStats: LobbyStats | null
    isLoading: boolean
    isRefreshing: boolean
    refreshStats: () => Promise<void>
    refreshUserCount: () => Promise<number>
    getUserCount: () => number
    getDefaultRooms: () => LobbyStats['defaultRooms']
    getCustomRooms: () => LobbyStats['customRooms']
}

export const useLobbyStats = (isActive: boolean = true, companyId?: string | null): UseLobbyStatsReturn => {
    const [lobbyStats, setLobbyStats] = useState<LobbyStats | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [subscriptionId, setSubscriptionId] = useState<string | null>(null)
    
    // Check if company is disabled to avoid unnecessary polling
    const { isDisabled: isCompanyDisabled } = useCompanyDisabledState()

    // Update company context on the lobby service
    useEffect(() => {
        try {
            if (typeof companyId !== 'undefined') {
                lobbyService.setCompanyId(companyId || null)
            }
        } catch {}
    }, [companyId])

    // Subscribe to lobby stats when active and company is not disabled
    useEffect(() => {
        if (!isActive || isCompanyDisabled) {
            // Cleanup subscription when not active or company is disabled
            if (subscriptionId) {
                lobbyService.unsubscribe(subscriptionId)
                setSubscriptionId(null)
            }
            if (isCompanyDisabled && process.env.NODE_ENV !== 'production') {
                console.log('[useLobbyStats] Skipping lobby stats polling - company is disabled')
            }
            return
        }

        setIsLoading(true)

        const id = lobbyService.subscribe((stats) => {
            setLobbyStats(stats)
            setIsLoading(false)
        })

        setSubscriptionId(id)

        return () => {
            if (id) {
                lobbyService.unsubscribe(id)
            }
        }
    }, [isActive, isCompanyDisabled])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (subscriptionId) {
                lobbyService.unsubscribe(subscriptionId)
            }
        }
    }, [])

    const refreshStats = useCallback(async () => {
        try {
            setIsRefreshing(true)
            await lobbyService.refreshStats()
        } catch (error) {
            console.error('Failed to refresh lobby stats:', error)
        } finally {
            setIsRefreshing(false)
        }
    }, [])

    const refreshUserCount = useCallback(async () => {
        try {
            setIsRefreshing(true)
            return await lobbyService.refreshUserCount()
        } catch (error) {
            console.error('Failed to refresh user count:', error)
            return 0
        } finally {
            setIsRefreshing(false)
        }
    }, [])

    const getUserCount = useCallback(() => {
        return lobbyStats?.totalUsers || 0
    }, [lobbyStats?.totalUsers])

    const getDefaultRooms = useCallback(() => {
        return lobbyStats?.defaultRooms || []
    }, [lobbyStats?.defaultRooms])

    const getCustomRooms = useCallback(() => {
        return lobbyStats?.customRooms || []
    }, [lobbyStats?.customRooms])

    return {
        lobbyStats,
        isLoading,
        isRefreshing,
        refreshStats,
        refreshUserCount,
        getUserCount,
        getDefaultRooms,
        getCustomRooms,
    }
} 